import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '@/shared/lib/apiClient';
import { Button, CrayonInput, NotebookSheet, useToastStore } from '@/shared/ui';

import { useParticipant } from '../hooks/useParticipant';
import {
  PARTICIPANT_CODE_EXAMPLE,
  participantCodeSchema,
  type ParticipantCodeFormValues,
} from '../schemas/participantCode.schema';

import styles from './ParticipantOnboarding.module.css';

interface ParticipantOnboardingProps {
  /**
   * 복원이 끝난 직후 호출되는 콜백.
   * 호출 측이 모달이라면 이때 모달을 닫는 용도로 사용한다.
   */
  onComplete?: () => void;
  /**
   * 코드 교체 흐름임을 명시하기 위한 플래그.
   * true면 안내 카피와 버튼 라벨이 "교체" 톤으로 바뀐다.
   * (현재 이 컴포넌트는 항상 코드를 이미 가진 사용자를 대상으로 하지만,
   *  외부 호출 시그니처 호환을 위해 prop은 유지한다.)
   */
  isReplacement?: boolean;
}

/**
 * 다른 디바이스에서 발급받은 참여자 코드를 입력해 진행 상태를 이어받는 화면.
 *
 * "새로 시작" 발급 흐름은 페이지 진입 시 자동으로 처리되므로, 이 컴포넌트는
 * 오로지 복원(이어서 하기)만 담당한다. 모달 안에서 호출되어 사용자가 다른
 * 디바이스의 코드를 입력하면 → 서버 조회 → 누적된 진행 상태를 통째로 가져와
 * 현재 디바이스의 상태를 교체한다.
 */
export function ParticipantOnboarding({
  onComplete,
  isReplacement = false,
}: ParticipantOnboardingProps): React.JSX.Element {
  const pushToast = useToastStore((s) => s.push);
  const { restore } = useParticipant();
  const inputElRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ParticipantCodeFormValues>({
    resolver: zodResolver(participantCodeSchema),
    mode: 'onSubmit',
  });

  // 마운트 시 입력창에 자동 포커스 — 모달이 열리면 사용자가 바로 타이핑할 수 있게 한다.
  useEffect(() => {
    inputElRef.current?.focus();
  }, []);

  /** 복원 폼 제출 — 서버 조회 → 진행 상태 교체. */
  const onSubmit = handleSubmit(async ({ code }) => {
    try {
      await restore.mutateAsync(code);
      pushToast({
        variant: 'success',
        message: isReplacement ? '코드를 교체했어요.' : '진행 상태를 불러왔어요.',
      });
      onComplete?.();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '코드를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.';
      pushToast({ variant: 'error', message });
      setError('code', { type: 'server', message });
    }
  });

  // register와 ref 병합 (CodeInputForm과 동일한 패턴).
  const { ref: hookRef, ...fieldProps } = register('code');

  return (
    <NotebookSheet marginColor="none" ruled={false} className={styles.card}>
      <header className={styles.heading}>
        <h2 className={styles.title}>이어서 하기</h2>
        <p className={styles.lead}>
          다른 디바이스에서 발급받은 6자리 참여자 코드를 입력하면, 그동안 모은 진행
          상태를 그대로 이어받을 수 있어요.
        </p>
      </header>

      <form onSubmit={onSubmit} className={styles.panel} noValidate>
        <CrayonInput
          {...fieldProps}
          ref={(el) => {
            hookRef(el);
            inputElRef.current = el;
          }}
          label="기존 참여자 코드"
          placeholder={PARTICIPANT_CODE_EXAMPLE}
          helperText={`예: ${PARTICIPANT_CODE_EXAMPLE}`}
          maxLength={12}
          error={Boolean(errors.code)}
          errorText={errors.code?.message}
          disabled={isSubmitting || restore.isPending}
        />
        <div className={styles.restoreActions}>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isSubmitting || restore.isPending}
          >
            {restore.isPending ? '불러오는 중…' : '이 코드로 이어서 하기'}
          </Button>
        </div>
      </form>
    </NotebookSheet>
  );
}
